import * as ng from 'angular';
import * as Linq from 'linq-es5';
import { Moment } from 'moment';
import { ComponentRegistration } from '../core';
import { Dto, IParkingMediaTypeResource } from '../data';
import {IParkingMediaType} from "../data/interfaces";
let template = require('./parking-media.html');

let MediaIdentifierDelimiter = ',';

// Note, DuplicateIdentifierErrorName becomes a variable in errors object. So the name should be alphanumeric.
let DuplicateIdentifierErrorName = 'duplicateIdentifier';

class ParkingMediaComponent extends ComponentRegistration {

    constructor() {
        super('parkingMedia', {
            controller: ParkingMediaController,
            templateUrl: template,
            controllerAs: 'parking',
            bindings: {
                selections: '=?',
                ngDisabled: "=?",
                singleSelect: "@?",
                required: "=?",
                form: "&"
            }
        });
    }
}

interface IParkingMediaController {

    // Selected parking media type Id in replace existing media mode.
    selectedIndex: string;
    // This is a resource to get the parking media types from API
    parkingMediaTypeId: IParkingMediaTypeResource;
    _onSingleSelectChange(): void;
    onMediaIdentifierChanged(mediaData: any) : void;
    onMultiSelectClick(): void;
    onSingleSelectClick(): void;
    buildMediaRequests(mediaTypes): any;
    loadParkingMediaTypes(): any;
    getFormControlHasErrors(controlName: string, errors: string[]) : boolean;
    getFormControl(controlName : string) : any;
}

class UserEnteredIdentifierData {

    public constructor(private _mediaIdentifierName : string, private _mediaIdentifier : string) {
        if (!this._mediaIdentifierName)
            this._mediaIdentifierName = '';
    }

    public get mediaIdentifierName() : string {
        return this._mediaIdentifierName;
    }

    public get mediaIdentifier() : string {
        if (this._validateDuplicateIdentifiers) {
            var validateDuplicateIdentifiers = this._validateDuplicateIdentifiers;
            this._validateDuplicateIdentifiers = null;

            // We need to run the validator only once, when "Replace Existing" is checked/unchecked
            validateDuplicateIdentifiers();
        }

        return this._mediaIdentifier;
    }

    public set mediaIdentifier(value : string) {
        this._mediaIdentifier = value;
    }

    private _validateDuplicateIdentifiers: () => void;

    public set validateDuplicateIdentifiers(validator: () => void) {
        this._validateDuplicateIdentifiers = validator;
    }
}

class MediaRequest {

    private _parsedMediaIdentifiers : UserEnteredIdentifierData[] = [];
    private _quantity: number;

    requestDateTime: string;
    recordVersion: string;
    replaceParkingMediaId: string;

    private _validationPattern : RegExp;
    private _mostRecentQuantity : number | null = null;

    public get subTotal(): number {
        return this.quantity * this.type.price;
    }

    // Note, we cannot just pass IParkingMediaController, since java script error is thrown that scope cannot be copied.
    // So we pass functions to get controller values that might cjhange.
    constructor(private _parkingMediaType : IParkingMediaType, mediaIdentifier : string, private _isSingleSelect :() => boolean, private _singleSelectSelectedIndexFunc: () => string | null,
                private _validateDuplicateIdentifiers: () => void) {

        this._validationPattern = new RegExp('^$|' + this._parkingMediaType.identifierValidationExpression);

        if (!mediaIdentifier)
            mediaIdentifier = '';
        else
            mediaIdentifier = mediaIdentifier.trim();

        if (mediaIdentifier) {
            let parsedMediaIdentifiers =  this.mediaIdentifier.split(MediaIdentifierDelimiter);

            for (var i : number = 0; i < parsedMediaIdentifiers.length; ++i)
                this._parsedMediaIdentifiers.push(new UserEnteredIdentifierData(this.parkingMediaTypeId + '-' + i, parsedMediaIdentifiers[i]));
        }
    }

    public get parsedMediaIdentifiers() : UserEnteredIdentifierData[] {

        let singleSelectQuantity = this.getSingleSelectQuantity();

        if (singleSelectQuantity !== null) {
            // In single select mode lets show either 1 or 0 identifier.
            let parsedMediaIdentifiers : UserEnteredIdentifierData[] = [];
            if (singleSelectQuantity === 1) {

                if (this._quantity === 0 && this.supportsUserEntry) {
                    // This will add item to array
                    this.quantity = 1;
                }

                parsedMediaIdentifiers.push(this._parsedMediaIdentifiers[0]);
            }

            return parsedMediaIdentifiers;
        }

        return this._parsedMediaIdentifiers;
    }

    public get supportsUserEntry(): boolean {
        return this._parkingMediaType.supportsUserEntry;
    }

    public get validationPattern() : RegExp {
        return this._validationPattern;
    }

    public get type() : IParkingMediaType {
        return this._parkingMediaType;
    }

    public get parkingMediaTypeId() : string {
        return this._parkingMediaType.id;
    }

    public get quantity(): number {

        let singleSelectQuantity = this.getSingleSelectQuantity();
        //_mostRecentQuantity
        let newQuantity = singleSelectQuantity === null ? this._quantity : singleSelectQuantity;

        // If quantity was not called to resize the media identifiers based on new quantity,
        // lets call the setter to properly initialize _parsedMediaIdentifiers (this might be the case when
        // ParkingMediaController.copyMediaRequests() is called on SingleSelect radio button click).
        if (this._parsedMediaIdentifiers.length < newQuantity)
            this.quantity = newQuantity;

        // When we uncheck "Replace Existing" check box, the user entered identifiers after the first one are never validated again, since
        // they are just re-bound to existing identifiers, if there were more then one identifier, before the checkbox was checked.
        // So lets make sure run the validator, if the count changed.
        if (newQuantity !== this._mostRecentQuantity) {
            var self = this;
            this._parsedMediaIdentifiers.forEach(x => {
                // The validators will run single time, when the input controls are re-bound again to models.
                x.validateDuplicateIdentifiers = self._validateDuplicateIdentifiers;
            });
        }

        this._mostRecentQuantity = newQuantity;
        return newQuantity;
    }

    public set quantity(value: number) {

        this._quantity = value;

        let numOfItemsToAdd = this._quantity - this._parsedMediaIdentifiers.length;

        if (numOfItemsToAdd < 0) {
            let numberOfItemsToRemove = -numOfItemsToAdd;

            this._parsedMediaIdentifiers.splice(this._parsedMediaIdentifiers.length - numberOfItemsToRemove, numberOfItemsToRemove);
        }
        else {
            for (var i: number = 0; i <  numOfItemsToAdd; ++i)
                this._parsedMediaIdentifiers.push(new UserEnteredIdentifierData(this.parkingMediaTypeId + '-' + this._parsedMediaIdentifiers.length, ''));
        }
    }

    public get mediaIdentifier() : string {

        // Generate a comma delimited media identifiers and assigns the value to _mediaIdentifier
        var aggregatedMediaIdentifier = '';

        let singleSelectQuantity = this.getSingleSelectQuantity();

        if (singleSelectQuantity) {
            // Note, this._parsedMediaIdentifiers is not expected to be empty (this.quantity resizes the array), however in
            // single select mode lets do this additional check just in case.
            return singleSelectQuantity === 1 && this._parsedMediaIdentifiers.length ? this._parsedMediaIdentifiers[0].mediaIdentifier.trim() : '';
        }

        let numberOfIdentifiers = this.quantity;

        for (var i : number = 0; i < numberOfIdentifiers; ++i) {

            let mediaIdentifier = this._parsedMediaIdentifiers[i].mediaIdentifier;

            if (!mediaIdentifier)
                mediaIdentifier = '';
            else
                mediaIdentifier = mediaIdentifier.trim();

            if (mediaIdentifier === '')
                continue;

            if (aggregatedMediaIdentifier.length > 0)
                aggregatedMediaIdentifier += MediaIdentifierDelimiter;

            aggregatedMediaIdentifier += mediaIdentifier;
        }

        return aggregatedMediaIdentifier;
    }

    private getSingleSelectQuantity() : number | null {
        // In single select the quantity is calculated. Lets return here to avoid any unpredicted changes to _parsedMediaIdentifiers
        // (say when the quantity control value changes to 1 or 0).

        if (this._isSingleSelect() === true) {
            // If nothing yet selected, _singleSelectSelectedIndexFunc() returns null, even if _isSingleSelect() is true.
            return this.parkingMediaTypeId === this._singleSelectSelectedIndexFunc() ? 1 : 0;
        }

        return null;
    }
}

class ParkingMediaController implements IParkingMediaController {
    private static $inject: Array<string> = ["$scope", "ParkingMediaType", "moment"];

    //private fields
    private mediaTypes: Array<Dto.IParkingMediaType> = [];
    private mediaRequests: Array<MediaRequest> = [];

    private hostForm: ng.IFormController;
    //bindings
    private selections: Array<any>;
    private ngDisabled: boolean;

    private form: Function;

    singleSelect: boolean = false;

    // This is actually parking media type identifier, which is string and not an index
    selectedIndex: string = null;

    constructor(private $scope: ng.IScope,
                private _parkingMediaType: IParkingMediaTypeResource,
                private moment: Moment) {

        console.log("MediaSelect controller loading.");
    }

    public $onInit(): void {
        this.ngDisabled = this.ngDisabled || false;
        this.singleSelect = this.singleSelect || false;
        this.selections = this.selections || [];

        this.hostForm = this.form();
        if (!this.hostForm) {
            throw "Form reference was not resolved by the execution of the expression.";
        }

        this.loadParkingMediaTypes();

        let self = this;
        this.$scope.$watch("parking.singleSelect", () => {
            self._onSingleSelectChange();
        }, true);

        console.log("MediaSelect controller loaded.");
    }

    public getFormControl(controlName : string) : any {
        return this.hostForm[controlName];
    }

    public getFormControlHasErrors(controlName: string, errors: string[]) : boolean {
        var errorsObject = this.getFormControl(controlName).$error;

        if (!errorsObject)
            return false;

        for (var i : number = 0; i < errors.length; ++i) {
            if (errorsObject.hasOwnProperty(errors[i]))
                return true;
        }

        return false;
    }

    public get parkingMediaTypeId(): IParkingMediaTypeResource {
        return this._parkingMediaType;
    }

    public get total() : number {
        return  Linq.From(this.mediaRequests).Sum((x) => x.subTotal);
    }

    private validate(): void {
        this.validateUserEnteredIdentifiers();
    }

    public _onSingleSelectChange(): void {
        // Lets assign something to this.selectedIndex.
        // Otherwise this value stays null, and no validation error is displayed.
        //We can add validator for the case, when nothing is selected (which is the same as there is no media type),
        // however it is not worth the effort, since normally the array this.mediaTypes will normally not be empty.
        // Also, latest we can make a change to re-select what was selected last time, instead of always selecting the first one,
        // However, this was always like this and no time to test the change.
        if (this.singleSelect && this.mediaTypes && this.mediaTypes.length > 0)
            this.selectedIndex = this.mediaTypes[0].id;
        else
            this.selectedIndex = null;

        this.copyMediaRequests();
    }

    public onMediaIdentifierChanged(mediaRequest: MediaRequest) : void {
        this.validateUserEnteredIdentifiers();
        this.copyMediaRequests();
    }

    public onMultiSelectClick(): void {
        this.copyMediaRequests();
    }

    public onSingleSelectClick(): void {
        this.copyMediaRequests();
    }

    private validateUserEnteredIdentifiers() : boolean {
        if (!this.mediaRequests)
            return true;

        // Lets validate that the identifier number is not entered multiple times.
        // Note, we check across all media identifiers for each user entered identifier.
        // We could have improved this slightly ad prohibit duplicate identifiers for media requests with the same media type.
        // However, I doubt we will ever have a scenario when the user enters two similar valid identifiers for two different transponder types.
        // First, different transponders have different formats. And second, the user will never have two
        // different transponders with the same identifier. Therefore, it is not worth an effort.

        var enteredMediaIdentifiers :string[] = [];
        var hasValidationErrors = false;

        for (var mediaRequestIndex : number = 0; mediaRequestIndex < this.mediaRequests.length; ++mediaRequestIndex) {
            var mediaRequest = this.mediaRequests[mediaRequestIndex];

            if (!mediaRequest.parsedMediaIdentifiers || !mediaRequest.supportsUserEntry)
                continue;

            for (var identifierIndex: number = 0; identifierIndex < mediaRequest.parsedMediaIdentifiers.length; ++identifierIndex) {
                var parsedIdentifier = mediaRequest.parsedMediaIdentifiers[identifierIndex];

                var parsedIdentifierControl = this.getFormControl(parsedIdentifier.mediaIdentifierName);

                if (!parsedIdentifierControl)
                    continue;

                var mediaIdentifierToLowerCase = parsedIdentifier.mediaIdentifier || '';

                if (enteredMediaIdentifiers.find(x => mediaIdentifierToLowerCase === x)) {
                    hasValidationErrors = true;
                    parsedIdentifierControl.$setValidity(DuplicateIdentifierErrorName, false);
                }
                else {
                    parsedIdentifierControl.$setValidity(DuplicateIdentifierErrorName, true);
                }

                enteredMediaIdentifiers.push(mediaIdentifierToLowerCase);
            }
        }

        return !hasValidationErrors;
    }

    /*
    Note, selections list is converted to IParkingMediaRequest, and is sent to the API in account-request.ts file.
    IParkingMediaRequest cannot have calculated properties, since the API call will fails (mediaRequests has calculated properties).
    Therefore unfortunately we have copy this.mediaRequests into slection, to get rid off calculate properties, when changes occur in UI.
    For now copying the values on changes is fast, however I will look into moving this code to the event handler,
    when working on other story.

    We need to hook up page leaving event, and copy the values once into selections variable (one option is to register a service, which
    will have onPageNext(), which will be called by wizards or media.ts. We can inject the service as a constructor parameter).
    * */
    private copyMediaRequests(): void {

        this.selections = [];

        ng.forEach(this.mediaRequests, (mediaRequest) => {
            this.selections.push({
                type: mediaRequest.type,
                parkingMediaTypeId: mediaRequest.parkingMediaTypeId,
                requestDateTime: mediaRequest.requestDateTime,
                quantity: mediaRequest.quantity,
                supportsUserEntry: mediaRequest.supportsUserEntry,
                mediaIdentifier: mediaRequest.mediaIdentifier,
                recordVersion: mediaRequest.recordVersion,
                replaceParkingMediaId: mediaRequest.replaceParkingMediaId
            });
        });
    }

    public buildMediaRequests(mediaTypes): any {

        this.mediaRequests = Linq.From(mediaTypes).Select((pmType: any) => {
            let mediaRequest = new MediaRequest(pmType,
                '', () => {
                    // singleSelect is actually a string, even though the type in controller is bool. The data binding
                    // results in value becoming a string.

                    return ('' + this.singleSelect) == 'true';
                },
                () => {
                    // selectedIndex is numeric in controller, but it becomes a string media type identifier.
                    // This can be changed later, when we get rid off these weird conversions.
                    return (this.selectedIndex == undefined || this.selectedIndex == null) ? null :  '' + this.selectedIndex;
                },
                // Note, passing this.validateUserEnteredIdentifiers will not work, since this will not be this object, when the
                // function is executed.
                () => this.validateUserEnteredIdentifiers());

            mediaRequest.requestDateTime = (this.moment as any)().format();
            mediaRequest.quantity = 0;
            mediaRequest.recordVersion = null;
            mediaRequest.replaceParkingMediaId = null;
            return mediaRequest;
        }).ToArray();
    }

    public loadParkingMediaTypes(): any {
        return this._parkingMediaType.query({})
            .$promise
            .then((response): any => {
                this.mediaTypes = response;
                this.buildMediaRequests(this.mediaTypes);
                this.selections = this.mediaRequests
            })
            .catch((e) => {
                console.log(e);
            });
    }
}

let pmediaComponent = new ParkingMediaComponent();
export default pmediaComponent;
export {pmediaComponent as ParkingMediaComponent, IParkingMediaController, ParkingMediaController}


// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/user-controls/parking-media.ts